import React, { Suspense, lazy, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from './components/Header';
import Footer from './components/Footer';
import ChatBot from './components/ChatBot';
import ErrorBoundary from './components/ErrorBoundary';

const Home = lazy(() => import('./pages/Home'));
const Solutions = lazy(() => import('./pages/Solutions'));
const Vision = lazy(() => import('./pages/Vision'));
const Contact = lazy(() => import('./pages/Contact'));
const About = lazy(() => import('./pages/About'));
const Policy = lazy(() => import('./pages/Policy'));
const Login = lazy(() => import('./pages/Login'));
const Webchat = lazy(() => import('./pages/Webchat'));
const NotFound = lazy(() => import('./pages/NotFound'));

const RouteFallback = () => (
  <div className="container">
    <div className="content">
      <section className="section-card" style={{ padding: '2rem', textAlign: 'center', color: '#a3dcff' }}>
        Loading…
      </section>
    </div>
  </div>
);

const LOGIN_WEBHOOK_URL =
  process.env.REACT_APP_LOGIN_WEBHOOK_URL ||
  'https://n8n.margue.net:5678/webhook/507cafbb-37b9-49d0-b2c8-201592bd85ec';

const App = () => {
  const [user, setUser] = useState(null);

  const handleLogin = async (username, password) => {
    try {
      const payload = { username, password, login: username };

      const response = await fetch(LOGIN_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Login server returned ${response.status}`);
      }

      const rawText = await response.text();
      if (!rawText || !rawText.trim()) {
        throw new Error('Login server returned an empty response');
      }

      let data;
      try {
        data = JSON.parse(rawText);
      } catch {
        throw new Error('Login server returned invalid JSON');
      }

      const result = Array.isArray(data) ? data[0] : data;

      if (result?.success && result?.user?.username) {
        setUser(result.user.username);
      } else {
        alert(`❌ ${result?.message || 'Login failed. Please check your credentials.'}`);
      }
    } catch (error) {
      alert(`No login server connection! (${error.message})`);
    }
  };

  return (
    <Router>
      <div id="root">
        <div className="App">
          <div className="video-background" aria-hidden="true">
            <video
              autoPlay
              loop
              muted
              playsInline
              preload="none"
              poster="/logo512.png"
            >
              <source src="/background.mp4" type="video/mp4" />
            </video>
          </div>
          <div className="video-overlay" aria-hidden="true"></div>
          <Helmet>
            <meta charSet="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <meta property="og:site_name" content="AI City" />
            <meta name="twitter:creator" content="@AICity" />
          </Helmet>
          <Header />
          <div className="content">
            <ErrorBoundary>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/solutions" element={<Solutions />} />
                  <Route path="/Solutions" element={<Navigate to="/solutions" replace />} />
                  <Route path="/vision" element={<Vision />} />
                  <Route path="/Vision" element={<Navigate to="/vision" replace />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/Contact" element={<Navigate to="/contact" replace />} />
                  {/* About page disabled — kept for later. Re-enable these routes to restore access. */}
                  {/* <Route path="/about" element={<About />} /> */}
                  {/* <Route path="/About" element={<Navigate to="/about" replace />} /> */}
                  <Route path="/policy" element={<Policy />} />
                  <Route path="/Policy" element={<Navigate to="/policy" replace />} />
                  <Route path="/mobile" element={<Navigate to="/" replace />} />
                  <Route path="/login" element={user ? <Navigate to="/webchat" /> : <Login onLogin={handleLogin} />} />
                  <Route path="/webchat" element={user ? <Webchat username={user} /> : <Navigate to="/login" />} />
                  <Route path="/Webchat" element={<Navigate to="/webchat" replace />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </div>
          <Footer />
          <ChatBot />
        </div>
      </div>
    </Router>
  );
};

export default App;
